/**
 * Contrôles de colonne du registre d'éditeurs.
 *
 * Chaque cellule rend la valeur d'une colonne dans le formulaire d'une ligne
 * de `CollectionEditor` :
 *  - mode `inline` : {id, value, onChange, placeholder, ...config} — saisie directe
 *  - mode `modal`  : {id, value, onClick, ...config} — aperçu qui ouvre l'éditeur du type
 *
 * Elles restent légères : aucun état interne, aucun chargement. Et elles
 * s'habillent avec les recettes du design system (`input`, `textarea`) plutôt
 * qu'avec des classes ad hoc, pour que la pop-in d'un champ `list` ressemble
 * aux autres formulaires de l'application.
 *
 * L'`id` reçu vient du `<label for>` du formulaire : il est posé sur le
 * contrôle lui-même (input, zone de texte ou bouton — tous étiquetables),
 * jamais sur un conteneur, sinon le libellé n'active rien.
 */
import { Image as ImageIco, Link2, List as ListIco, Square as Empty } from 'lucide-react'
import { IconPicker, IconComponent } from './IconPicker'
import { TokenTextField } from './TokenTextField'

/** Aperçu d'une valeur composée : bouton pleine largeur, vignette + résumé. */
const previewButton =
  'builder-collection-preview'

// --- cellules `inline` ------------------------------------------------------

/**
 * Une colonne déclarée `tokens:` se saisit avec l'insertion de champs (« @ ») —
 * mais seulement si le formulaire a bien livré des champs à dérouler. Sinon on
 * retombe sur le contrôle nu : une déclaration qui ne résout rien ne doit pas
 * priver l'éditeur de son champ de texte.
 */
const hasTokens = (tokens) => Array.isArray(tokens) && tokens.length > 0

export const TextCell = ({ id, value, onChange, placeholder, tokens, label }) => (
  hasTokens(tokens) ? (
    <TokenTextField
      id={id}
      value={value}
      onChange={onChange}
      tokens={tokens}
      label={label}
      placeholder={placeholder}
    />
  ) : (
    <input
      id={id}
      type="text"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder ?? ''}
      className="input"
    />
  )
)

export const TextareaCell = ({ id, value, onChange, placeholder, rows, tokens, label }) => (
  hasTokens(tokens) ? (
    <TokenTextField
      id={id}
      value={value}
      onChange={onChange}
      tokens={tokens}
      label={label}
      placeholder={placeholder}
      rows={rows ?? 4}
      multiline
    />
  ) : (
    <textarea
      id={id}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder ?? ''}
      rows={rows ?? 4}
      className="textarea"
    />
  )
)

export const IconCell = ({ id, value, onChange }) => (
  <IconPicker id={id} value={value || 'Star'} onChange={onChange} />
)

// --- cellules `modal` (aperçu qui ouvre l'éditeur du type) ------------------

export const ImageDrupalCell = ({ id, value, onClick }) => {
  const url = value?.url
  return (
    <button id={id} type="button" onClick={onClick} className={previewButton}>
      {url ? (
        <img src={url} alt="" className="builder-collection-thumb" />
      ) : (
        <span className="builder-collection-thumb builder-collection-thumb-empty">
          <ImageIco size={15} aria-hidden="true" />
        </span>
      )}
      <span className="builder-collection-previewtext">
        {url ? (value.alt || 'Modifier l’image') : 'Choisir une image'}
      </span>
    </button>
  )
}

export const LinkCell = ({ id, value, onClick }) => {
  const url = typeof value === 'string' ? value : value?.url
  return (
    <button id={id} type="button" onClick={onClick} className={previewButton}>
      <span className="builder-collection-thumb builder-collection-thumb-empty">
        <Link2 size={15} aria-hidden="true" />
      </span>
      <span className="builder-collection-previewtext">
        {url || 'Définir l’adresse'}
      </span>
    </button>
  )
}

export const CollectionCell = ({ id, value, onClick }) => {
  const arr = Array.isArray(value) ? value : []
  return (
    <button id={id} type="button" onClick={onClick} className={previewButton}>
      <span className="builder-collection-thumb builder-collection-thumb-empty">
        <ListIco size={15} aria-hidden="true" />
      </span>
      <span className="builder-collection-previewtext">
        {arr.length === 0
          ? 'Aucun élément — remplir'
          : `${arr.length} ${arr.length > 1 ? 'éléments' : 'élément'} — modifier`}
      </span>
    </button>
  )
}

export const GenericModalCell = ({ id, value, onClick }) => {
  const summary = typeof value === 'string' && value
    ? value
    : (value ? 'Défini — modifier' : 'Non défini')
  return (
    <button id={id} type="button" onClick={onClick} className={previewButton}>
      <span className="builder-collection-thumb builder-collection-thumb-empty">
        <Empty size={15} aria-hidden="true" />
      </span>
      <span className="builder-collection-previewtext">{summary}</span>
    </button>
  )
}

/** Repli d'affichage quand l'icône est utilisée en mode `modal` (rare). */
export const IconPreviewCell = ({ id, value, onClick }) => (
  <button id={id} type="button" onClick={onClick} className={previewButton}>
    <span className="builder-collection-thumb builder-collection-thumb-empty">
      <IconComponent name={value || 'Star'} size={16} />
    </span>
    <span className="builder-collection-previewtext">{value || 'Star'}</span>
  </button>
)
